import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiFloorTenantCompanyService } from '../../../../../core/services/api/provider-parking/api-floor-tenant-company.service';
import { FloorTenantCompany } from '../../../../../core/models/provider-parking/floor-tenant-company.model';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalFloorTenantCompanyComponent } from '../../../modals/provider-parking/modal-floor-tenant-company/modal-floor-tenant-company.component';
import { ToastService } from 'angular-toastify';

@Component({
  selector: 'app-floor-tenant-company',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalFloorTenantCompanyComponent],
  templateUrl: './floor-tenant-company.component.html',
  styleUrl: './floor-tenant-company.component.css'
})
export class FloorTenantCompanyComponent implements OnInit {
  public list: FloorTenantCompany[] = [];
  public filteredList: FloorTenantCompany[] = [];
  public isLoading: boolean = false;

  // Management State
  public isModalOpen: boolean = false;
  public selectedItem: FloorTenantCompany | null = null;
  public selectedIds: Set<number> = new Set();

  // Search & Filter State
  public searchText: string = '';
  public filterArea: string = '';
  public filterStatus: string = ''; // 'online', 'offline', ''
  public filterMinLots: number | null = null;
  public sortColumn: string = 'name';
  public sortDirection: 'asc' | 'desc' = 'asc';

  // UI Dropdown States
  public isAreaDropdownOpen: boolean = false;
  public isStatusDropdownOpen: boolean = false;

  public areaOptions: string[] = [];

  // Pagination State
  public currentPage: number = 1;
  public pageSize: number = 10;
  public totalItems: number = 0;

  // Dashboard Stats
  public stats = [
    { title: 'ชั้นทั้งหมด', value: 0, icon: 'layers', color: 'text-indigo-600 bg-indigo-50' },
    { title: 'จำนวน Lot ทั้งหมด', value: 0, icon: 'meeting_room', color: 'text-emerald-600 bg-emerald-50' },
    { title: 'พื้นที่ให้บริการ', value: 0, icon: 'place', color: 'text-amber-600 bg-amber-50' },
    { title: 'สถานะ Online', value: 0, icon: 'cloud_done', color: 'text-rose-600 bg-rose-50' }
  ];

  constructor(
    private apiService: ApiFloorTenantCompanyService,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.fetchData();
  }

  public fetchData(): void {
    this.isLoading = true;
    this.apiService.getFloorTenantCompanies().subscribe({
      next: (res) => {
        this.list = res || [];
        if (this.list.length === 0) this.loadMockData();
        this.updateFilterOptions();
        this.applyFilters();
        this.calculateStats();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Fetch error:', err);
        this.loadMockData();
        this.updateFilterOptions();
        this.applyFilters();
        this.calculateStats();
        this.isLoading = false;
      }
    });
  }

  private loadMockData(): void {
    this.list = [
      { id: 1, name: 'ชั้น 1-4', parkingType: 'อาคารจอดรถยนต์', lots: 1533, serviceArea: 'อาคารจอดรถ The Market 1', image1: 'https://placehold.co/600x400', image2: 'https://placehold.co/600x400', image3: 'https://placehold.co/600x400', isOnline: true },
      { id: 2, name: 'ชั้น 1', parkingType: 'อาคารจอดรถมอเตอร์ไซค์', lots: 235, serviceArea: 'อาคารจอดรถ The Market 1', image1: '', image2: '', image3: '', isOnline: false },
      { id: 3, name: 'ชั้น 2U', parkingType: 'อาคารจอดรถยนต์', lots: 0, serviceArea: 'อาคารจอดรถ The Market 2', image1: '', image2: '', image3: '', isOnline: false }
    ];
  }

  private updateFilterOptions(): void {
    this.areaOptions = Array.from(new Set(this.list.map(f => f.serviceArea))).sort();
  }

  private calculateStats(): void {
    this.stats[0].value = this.list.length;
    this.stats[1].value = this.list.reduce((acc, curr) => acc + curr.lots, 0);
    this.stats[2].value = new Set(this.list.map(f => f.serviceArea)).size;
    this.stats[3].value = this.list.filter(f => f.isOnline).length;
  }

  public applyFilters(): void {
    let result = [...this.list];

    // Search Text Filter
    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      result = result.filter(v =>
        v.name.toLowerCase().includes(search) ||
        v.parkingType.toLowerCase().includes(search) ||
        v.serviceArea.toLowerCase().includes(search)
      );
    }

    // Area Filter
    if (this.filterArea) {
      result = result.filter(v => v.serviceArea === this.filterArea);
    }

    // Status Filter
    if (this.filterStatus) {
      result = result.filter(v => 
        this.filterStatus === 'online' ? v.isOnline : !v.isOnline
      );
    }

    // Lots Filter (Minimum)
    if (this.filterMinLots !== null && this.filterMinLots !== undefined) {
      result = result.filter(v => v.lots >= (this.filterMinLots ?? 0));
    }

    // Sorting
    if (this.sortColumn) {
      result.sort((a, b) => {
        const valA = (a as any)[this.sortColumn];
        const valB = (b as any)[this.sortColumn];
        
        let comparison = 0;
        if (typeof valA === 'string') {
          comparison = valA.localeCompare(valB);
        } else {
          comparison = valA - valB;
        }
        
        return this.sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    this.totalItems = result.length;
    this.filteredList = result;
  }

  public sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applyFilters();
  }

  // --- UI Filter Actions ---
  public toggleAreaDropdown(): void {
    this.isAreaDropdownOpen = !this.isAreaDropdownOpen;
    this.isStatusDropdownOpen = false;
  }

  public toggleStatusDropdown(): void {
    this.isStatusDropdownOpen = !this.isStatusDropdownOpen;
    this.isAreaDropdownOpen = false;
  }

  public selectAreaFilter(area: string): void {
    this.filterArea = area;
    this.isAreaDropdownOpen = false;
    this.applyFilters();
  }

  public selectStatusFilter(status: string): void {
    this.filterStatus = status;
    this.isStatusDropdownOpen = false;
    this.applyFilters();
  }

  // --- Actions ---
  public toggleAll(event: any): void {
    if (this.selectedIds.size === this.filteredList.length && this.filteredList.length > 0) {
      this.selectedIds.clear();
    } else {
      this.filteredList.forEach(i => this.selectedIds.add(i.id));
    }
  }

  public toggleItem(id: number): void {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  public openCreateModal(): void {
    this.selectedItem = null;
    this.isModalOpen = true;
  }

  public openEditModal(item: FloorTenantCompany): void {
    this.selectedItem = { ...item };
    this.isModalOpen = true;
  }

  public closeModal(): void {
    this.isModalOpen = false;
    this.selectedItem = null;
  }

  public onRefresh(): void {
    this.fetchData();
  }

  public toggleStatusLive(item: FloorTenantCompany): void {
    const originalStatus = item.isOnline;
    item.isOnline = !item.isOnline;
    
    // Provide immediate UI feedback (simulating API call)
    // Normally you would call this.apiService.updateFloorTenantCompany(item).subscribe({...})
    setTimeout(() => {
      this.toast.success(`เปลี่ยนสถานะเป็น ${item.isOnline ? 'Online' : 'Offline'} สำเร็จ`);
      this.calculateStats(); // Ensure dashboard numbers are updated
      
      // Update the main list as well if it's a reference issue
      const index = this.list.findIndex(x => x.id === item.id);
      if (index !== -1) {
        this.list[index].isOnline = item.isOnline;
      }
    }, 400);
  }
}
